import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Search, Loader2, Navigation, Clock } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { reverseGeocode, getCurrentLocation } from '@/utils/location';
import { getPreferences } from '@/services/ConsentService';

export interface LocationData {
    name: string;
    lat: number;
    lon: number;
    country: string;
    timezone?: string; // We might need to fetch this separately or guess it
    display_name?: string;
}

interface LocationSelectorProps {
    onLocationSelect: (location: LocationData) => void;
    defaultValue?: string;
    className?: string;
    theme?: 'mystic' | 'dark';
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
    onLocationSelect,
    defaultValue = '',
    className = '',
    theme = 'mystic'
}) => {
    interface NominatimResult {
        place_id: number;
        lat: string;
        lon: string;
        display_name: string;
        name: string;
        address?: {
            country?: string;
        };
    }

    const [query, setQuery] = useState(defaultValue);
    const [results, setResults] = useState<NominatimResult[]>([]);
    const [recentLocations, setRecentLocations] = useState<LocationData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Load recent locations on mount (guarded by functional consent)
    useEffect(() => {
        if (!getPreferences().functional) return;
        const stored = localStorage.getItem('spirit_recent_locations');
        if (stored) {
            try {
                setRecentLocations(JSON.parse(stored));
            } catch (e) {
                console.error('Failed to parse recent locations', e);
            }
        }
    }, []);

    const saveRecentLocation = (location: LocationData) => {
        const updated = [location, ...recentLocations.filter(l => l.name !== location.name)].slice(0, 5);
        setRecentLocations(updated);
        
        // Only persist to local storage if functional consent is granted
        if (getPreferences().functional) {
            localStorage.setItem('spirit_recent_locations', JSON.stringify(updated));
        }
    };

    // Update dropdown position
    const updatePosition = () => {
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    // Update position on scroll/resize when open
    useEffect(() => {
        if (isOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    // Debounce the search query
    const debouncedQuery = useDebounce(query, 500);

    // Sync query with defaultValue when it changes (e.g., from automatic geolocation)
    useEffect(() => {
        if (defaultValue !== query) {
            setQuery(defaultValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    useEffect(() => {
        const fetchLocations = async () => {
            if (!debouncedQuery || debouncedQuery.length < 3) {
                setResults([]);
                return;
            }

            setIsLoading(true);
            try {
                // Using OpenStreetMap Nominatim API
                // Must provide a User-Agent as per their usage policy
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(debouncedQuery)}&limit=5`,
                    {
                        headers: {
                            'User-Agent': 'SpiritResearchLab/1.0'
                        }
                    }
                );
                const data = await response.json();
                setResults(data);
                // Only auto-open if results are found and the input is currently focused
                if (data.length > 0 && document.activeElement === wrapperRef.current?.querySelector('input')) {
                    setIsOpen(true);
                }
            } catch (error) {
                console.error('Failed to fetch locations:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLocations();
    }, [debouncedQuery]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = async (item: NominatimResult) => {
        setIsLoading(true);
        const lat = parseFloat(item.lat);
        const lon = parseFloat(item.lon);

        const geocodeResult = await reverseGeocode(lat, lon);

        const location: LocationData = {
            name: item.name,
            lat,
            lon,
            country: item.address?.country || extractCountry(item.display_name),
            display_name: item.display_name,
            timezone: geocodeResult?.timezone
        };

        setQuery(item.display_name.split(',')[0]); // Use shorter name for display
        saveRecentLocation(location);
        onLocationSelect(location);
        setIsOpen(false);
        setIsLoading(false);
    };

    const handleCurrentLocation = async () => {
        setIsLoading(true);
        setIsOpen(false);
        try {
            const coords = await getCurrentLocation();
            if (coords) {
                const geo = await reverseGeocode(coords.lat, coords.lon);
                if (geo) {
                    const location: LocationData = {
                        name: geo.name,
                        lat: geo.lat,
                        lon: geo.lon,
                        country: geo.country,
                        display_name: geo.display_name,
                        timezone: geo.timezone
                    };
                    setQuery(location.name);
                    saveRecentLocation(location);
                    onLocationSelect(location);
                }
            }
        } catch (error) {
            console.error('Failed to get current location:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRecentSelect = (location: LocationData) => {
        setQuery(location.name);
        // Move to top of recent
        saveRecentLocation(location);
        onLocationSelect(location);
        setIsOpen(false);
    };

    const extractCountry = (displayName: string) => {
        const parts = displayName.split(', ');
        return parts[parts.length - 1];
    };

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <div className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                    placeholder="חפש עיר או מקום..."
                    className={`w-full pl-4 pr-10 h-[38px] rounded-xl focus:ring-2 focus:ring-gold-500/50 focus:border-gold-500 transition-all text-white text-sm placeholder-mystic-500 ${
                        theme === 'dark'
                            ? 'bg-[#0a0514]/60 border border-white/10'
                            : 'bg-mystic-900/50 border border-mystic-700'
                    }`}
                    dir="rtl"
                />
                <div className="absolute top-1/2 right-3 -translate-y-1/2 pointer-events-none text-mystic-400">
                    {isLoading ? <Loader2 className="animate-spin w-4 h-4" /> : <Search className="w-4 h-4" />}
                </div>
            </div>

            {isOpen && (results.length > 0 || !query) && createPortal(
                <div
                    ref={dropdownRef}
                    style={{
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`
                    }}
                    className={`absolute z-[9999] mt-2 backdrop-blur-md rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto ${
                        theme === 'dark'
                            ? 'bg-[#0c071d]/98 border border-white/10'
                            : 'bg-mystic-900/95 border border-mystic-700'
                    }`}
                >
                    {results.length > 0 ? (
                        results.map((item) => (
                            <button
                                key={item.place_id}
                                onClick={() => handleSelect(item)}
                                className="w-full text-right px-4 py-3 hover:bg-gold-500/10 transition-colors flex items-start gap-3 group border-b border-white/5 last:border-0"
                            >
                                <MapPin className="w-4 h-4 mt-1 text-gold-500/50 group-hover:text-gold-500 shrink-0" />
                                <div>
                                    <div className="text-white font-medium group-hover:text-gold-400 transition-colors">
                                        {item.name || item.display_name.split(',')[0]}
                                    </div>
                                    <div className="text-xs text-mystic-400 mt-0.5 max-w-[250px] truncate">
                                        {item.display_name}
                                    </div>
                                </div>
                            </button>
                        ))
                    ) : (
                        <div className="p-2 space-y-1">
                            {/* Current Location Option */}
                            <button
                                onClick={handleCurrentLocation}
                                className="w-full text-right px-4 py-3 hover:bg-gold-500/10 transition-colors flex items-center gap-3 group rounded-lg text-gold-400 font-bold"
                            >
                                <Navigation className="w-4 h-4 text-gold-500 shrink-0" />
                                <div>השתמש במיקום הנוכחי</div>
                            </button>

                            {/* Recent Locations */}
                            {recentLocations.length > 0 && (
                                <>
                                    <div className="px-4 py-2 text-[11px] text-mystic-500 font-bold uppercase tracking-wider">
                                        אחרונים
                                    </div>
                                    {recentLocations.map((loc, idx) => (
                                        <button
                                            key={`${loc.name}-${idx}`}
                                            onClick={() => handleRecentSelect(loc)}
                                            className="w-full text-right px-4 py-2.5 hover:bg-white/5 transition-colors flex items-center gap-3 group rounded-lg"
                                        >
                                            <Clock className="w-3.5 h-3.5 text-mystic-600 group-hover:text-gold-500/70 shrink-0" />
                                            <div className="text-mystic-300 group-hover:text-white transition-colors">
                                                {loc.name}
                                            </div>
                                        </button>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};
